import React, { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { getSites } from "../api";
import { tx as __tx } from "../lib/tx";

const STATIC_PAGES = (t) => [
  { id: "page-/", label: t.nav.dashboard, sub: "// overview", path: "/", group: "pages" },
  { id: "page-/sites", label: t.nav.sites, sub: "// sites · deploys", path: "/sites", group: "pages" },
  { id: "page-/pbn", label: t.nav.pbn, sub: "// network · graph", path: "/pbn", group: "pages" },
  { id: "page-/audit", label: t.nav.audit, sub: "// score · issues", path: "/audit", group: "pages" },
  { id: "page-/articles", label: t.nav.articles, sub: "// ai · content", path: "/articles", group: "pages" },
  { id: "page-/promotion", label: t.nav.promotion, sub: "// indexnow · webhooks", path: "/promotion", group: "pages" },
  { id: "page-/automation", label: t.nav.automation, sub: "// scheduler · telegram", path: "/automation", group: "pages" },
  { id: "page-/keywords", label: t.nav.keywords, sub: "// queries · serp", path: "/keywords", group: "pages" },
  { id: "page-/ranks", label: t.nav.ranks, sub: "// positions · trends", path: "/ranks", group: "pages" },
  { id: "page-/reports", label: t.nav.reports, sub: "// export · pdf", path: "/reports", group: "pages" },
  { id: "page-/settings", label: t.nav.settings || __tx("Settings"), sub: "// api · keys", path: "/settings", group: "pages" },
];

export default function CommandPalette({ open, onOpenChange }) {
  const t = useT();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sites, setSites] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset state on open and load sites
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    let cancelled = false;
    getSites()
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res?.data) ? res.data : [];
        setSites(arr);
      })
      .catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const items = useMemo(() => {
    const pages = STATIC_PAGES(t);
    const siteItems = sites.map((s) => ({
      id: `site-${s.id || s._id}`,
      label: s.url || s.domain || s.name || "site",
      sub: `// site · ${s.id || s._id || ""}`.trim(),
      path: `/sites?focus=${encodeURIComponent(s.id || s._id || "")}`,
      group: "sites",
    }));
    const all = [...pages, ...siteItems];
    if (!query.trim()) return all;
    const q = query.toLowerCase();
    return all.filter((it) =>
      it.label.toLowerCase().includes(q) ||
      it.sub.toLowerCase().includes(q)
    );
  }, [query, sites, t]);

  // Clamp active index when items change
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, items.length - 1)));
  }, [items.length]);

  const select = (item) => {
    if (!item) return;
    onOpenChange(false);
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(items[active]);
    }
  };

  // Scroll active into view
  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-cmd-idx="${active}"]`);
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [active]);

  const groups = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      if (!map[it.group]) map[it.group] = [];
      map[it.group].push(it);
    });
    return map;
  }, [items]);

  const groupOrder = ["pages", "sites"];
  const groupLabels = { pages: "// pages", sites: "// sites" };

  let runningIdx = -1;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="xp-cmd-overlay" />
        <DialogPrimitive.Content
          className="xp-cmd"
          aria-label={__tx("Command palette")}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">{__tx("Command palette")}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{__tx("Search across pages and sites")}</DialogPrimitive.Description>
          <div className="xp-cmd-bar">
            <span className="xp-cmd-icon" aria-hidden="true">⌕</span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t.top?.searchPlaceholder || "Search…"}
              className="xp-cmd-input"
              aria-label={__tx("Search")}
              autoComplete="off"
            />
            <span className="xp-cmd-kbd">{__tx("esc")}</span>
          </div>
          <div className="xp-cmd-list" ref={listRef} role="listbox">
            {items.length === 0 ? (
              <div className="xp-cmd-empty">{__tx("no matches")}</div>
            ) : (
              groupOrder.map((g) => {
                if (!groups[g]?.length) return null;
                return (
                  <div key={g} className="xp-cmd-group">
                    <div className="xp-cmd-group-label">{groupLabels[g]}</div>
                    {groups[g].map((it) => {
                      runningIdx += 1;
                      const idx = runningIdx;
                      const isActive = idx === active;
                      return (
                        <button
                          key={it.id}
                          type="button"
                          data-cmd-idx={idx}
                          className={`xp-cmd-item ${isActive ? "on" : ""}`}
                          onMouseEnter={() => setActive(idx)}
                          onClick={() => select(it)}
                          role="option"
                          aria-selected={isActive}
                        >
                          <span className="xp-cmd-item-label">{it.label}</span>
                          <span className="xp-cmd-item-sub">{it.sub}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          <div className="xp-cmd-foot">
            <span><kbd>↑↓</kbd> {__tx("navigate")}</span>
            <span><kbd>↵</kbd> {__tx("open")}</span>
            <span><kbd>{__tx("esc")}</kbd> {__tx("close")}</span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
